import { isReadyWithData } from "../../../../components/utils/isResponseReady";
import useRouterVariable from "../../../../components/utils/useRouterVariable";
import {
  useAmILoggedInAgencyContact,
  useFetchOrgAtsEnabled,
  useGetCareerPageJobs,
} from "../../../api-docs";
import { usePublicJobsForAgencyContactQuery } from "../../../graphql.types";
import { SELF_APPLY_USER_TYPE_ENUM } from "../../types/self-apply-user-type";
import { EmptyCareersPage } from "../../pages/job-list-page/empty";
import { LoadingCareersPage } from "modules/self-apply/pages/job-list-page/loading";

const EMPTY_ARRAY = [];

export const SelfApplyGuard = (props: {
  children: React.ReactNode;
  selfApplyUserType: SELF_APPLY_USER_TYPE_ENUM;
}) => {
  const agencyLogin = useAmILoggedInAgencyContact({
    query: {
      enabled: props.selfApplyUserType === "agency",
      retry: false,
    },
  });

  const org = useRouterVariable("org", (o) => o as string);
  const orgAtsEnabled = useFetchOrgAtsEnabled(
    {
      orgFriendlyName: org as string,
    },
    {
      query: {
        enabled: org != null,
      },
    },
  );

  const shouldFireJobQuery =
    org != null && org != "" && orgAtsEnabled.isSuccess && orgAtsEnabled.data;

  const candidateJobs = useGetCareerPageJobs(org ?? "", {
    query: {
      enabled: shouldFireJobQuery && props.selfApplyUserType === "candidate",
    },
  });

  const agencyJobs = usePublicJobsForAgencyContactQuery(undefined, {
    enabled: shouldFireJobQuery && props.selfApplyUserType === "agency",
  });

  const jobsResponseIsLoading =
    props.selfApplyUserType === "candidate"
      ? candidateJobs.isLoading
      : agencyJobs.isLoading;

  const jobsResponseReady =
    props.selfApplyUserType === "candidate"
      ? isReadyWithData(candidateJobs)
      : isReadyWithData(agencyJobs);

  if (
    props.selfApplyUserType === "agency" &&
    agencyLogin.isFetched &&
    (agencyLogin.isError || !agencyLogin.data?.success)
  ) {
    return (
      <EmptyCareersPage
        needsAgencyLogin
        userType={props.selfApplyUserType}
        showSignInButton
      />
    );
  }

  if (!isReadyWithData(orgAtsEnabled) || jobsResponseIsLoading) {
    return <LoadingCareersPage />;
  }

  const availableJobs =
    (props.selfApplyUserType === "candidate"
      ? candidateJobs.data
      : agencyJobs?.data?.publicJobsForAgencyContact) ?? EMPTY_ARRAY;

  const hasJobs = jobsResponseReady && availableJobs.length > 0;

  if (!orgAtsEnabled.data || !hasJobs) {
    return (
      <EmptyCareersPage userType={props.selfApplyUserType} showSignInButton />
    );
  }

  return <>{props.children}</>;
};
